import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, PencilSimple, Trash, X, ShieldCheck } from "@phosphor-icons/react";

function RoleFormDrawer({ open, editing, catalog, branchTypes, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [perms, setPerms] = useState(new Set());
  const [scope, setScope] = useState("branch");
  const [btCodes, setBtCodes] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name || "");
      setDesc(editing.description || "");
      setPerms(new Set(editing.permissions || []));
      setScope(editing.scope || "branch");
      setBtCodes(new Set(editing.branch_type_codes || []));
    } else {
      setName("");
      setDesc("");
      setPerms(new Set());
      setScope("branch");
      setBtCodes(new Set());
    }
  }, [open, editing]);

  if (!open) return null;

  // 4 canonical verbs; everything else (verify / reject / cancel / approve
  // / escalation_manage / salary_view / etc.) is a workflow or sensitive
  // permission rendered as a pill below the module grid row.
  const VERBS = ["view", "create", "manage", "delete"];
  const isLegacyPerm = (fullKey) => Boolean(catalog?.legacy_map?.[fullKey]);

  const toggle = (k) => {
    setPerms((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  };

  const toggleBtCode = (code) => {
    setBtCodes((prev) => {
      const n = new Set(prev);
      if (n.has(code)) n.delete(code);
      else n.add(code);
      return n;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (scope === "branch_type" && btCodes.size === 0) {
      toast.error("Pick at least one branch type for Branch-type scope");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name,
        description: desc,
        permissions: Array.from(perms),
        scope,
        branch_type_codes: scope === "branch_type" ? Array.from(btCodes) : [],
      };
      if (editing) await api.patch(`/roles/${editing.id}`, body);
      else await api.post("/roles", body);
      toast.success(editing ? "Role updated" : "Role created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const isBuiltinAdmin = editing?.is_builtin && editing?.name === "admin";
  const hasLegacyPerms = editing?.has_legacy_perms;

  // Build the module × verb grid, extracting workflow perms (anything not
  // in V/C/M/D) as separate pills shown below the row.
  const buildModuleGrid = (mod, actions) => {
    const rows = [];
    const workflows = [];
    for (const [action, label] of Object.entries(actions)) {
      const key = `${mod}.${action}`;
      if (isLegacyPerm(key)) continue;  // legacy perms hidden from new grid
      if (VERBS.includes(action)) rows.push([action, key, label]);
      else workflows.push([action, key, label]);
    }
    const byVerb = Object.fromEntries(
      rows.map(([action, key, label]) => [action, [key, label]])
    );
    return { byVerb, workflows };
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        data-testid="role-form-drawer"
        className="w-[560px] bg-canvas-card border-l-2 border-ink overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-canvas-line flex items-center justify-between sticky top-0 bg-canvas-card z-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
              {editing ? "Edit role" : "New role"}
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1">
              {editing ? editing.name : "Custom role"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 border border-canvas-line hover:border-ink">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Role name
            </label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="role-name"
              disabled={editing?.is_builtin}
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink disabled:opacity-60"
            />
            {editing?.is_builtin && (
              <p className="font-mono text-[10px] text-ink-subtle mt-1">
                Built-in role names cannot be changed.
              </p>
            )}
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Description
            </label>
            <input
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              data-testid="role-description"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            />
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1.5">
              Data scope
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="role-scope-picker">
              <label
                className={`cursor-pointer border p-3 flex items-start gap-2 ${
                  scope === "branch" ? "border-ink bg-ink/5" : "border-canvas-line hover:border-ink"
                }`}
              >
                <input
                  type="radio"
                  name="role-scope"
                  value="branch"
                  checked={scope === "branch"}
                  onChange={() => setScope("branch")}
                  data-testid="role-scope-branch"
                  className="mt-0.5 accent-ink"
                />
                <span>
                  <span className="block font-mono text-[11px] uppercase tracking-wider font-semibold">
                    Branch-locked
                  </span>
                  <span className="block text-[11px] text-ink-muted mt-0.5">
                    One branch only. Default for shop-floor staff.
                  </span>
                </span>
              </label>
              <label
                className={`cursor-pointer border p-3 flex items-start gap-2 ${
                  scope === "branch_type" ? "border-ink bg-ink/5" : "border-canvas-line hover:border-ink"
                }`}
              >
                <input
                  type="radio"
                  name="role-scope"
                  value="branch_type"
                  checked={scope === "branch_type"}
                  onChange={() => setScope("branch_type")}
                  data-testid="role-scope-branch-type"
                  className="mt-0.5 accent-ink"
                />
                <span>
                  <span className="block font-mono text-[11px] uppercase tracking-wider font-semibold">
                    Branch-type
                  </span>
                  <span className="block text-[11px] text-ink-muted mt-0.5">
                    Every branch of the picked type(s) — e.g. all AD sub-dealers or all Service workshops.
                  </span>
                </span>
              </label>
              <label
                className={`cursor-pointer border p-3 flex items-start gap-2 ${
                  scope === "all_branches" ? "border-ink bg-ink/5" : "border-canvas-line hover:border-ink"
                }`}
              >
                <input
                  type="radio"
                  name="role-scope"
                  value="all_branches"
                  checked={scope === "all_branches"}
                  onChange={() => setScope("all_branches")}
                  data-testid="role-scope-all"
                  className="mt-0.5 accent-ink"
                />
                <span>
                  <span className="block font-mono text-[11px] uppercase tracking-wider font-semibold">
                    All branches
                  </span>
                  <span className="block text-[11px] text-ink-muted mt-0.5">
                    Every branch across the network. For Accounts / Audit / central Registrations.
                  </span>
                </span>
              </label>
            </div>
            {scope === "branch_type" && (
              <div className="mt-3 border border-canvas-line p-3" data-testid="role-btype-picker">
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-2">
                  Pick branch type(s) · {btCodes.size} selected
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(branchTypes || []).map((bt) => (
                    <label key={bt.code} className="flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={btCodes.has(bt.code)}
                        onChange={() => toggleBtCode(bt.code)}
                        data-testid={`role-btype-${bt.code}`}
                        className="accent-ink"
                      />
                      <span>
                        <span className="font-mono text-[10px] uppercase tracking-wider">{bt.code}</span>
                        <span className="block text-[11px] text-ink-muted">{bt.name}</span>
                      </span>
                    </label>
                  ))}
                  {(!branchTypes || branchTypes.length === 0) && (
                    <span className="text-[11px] text-ink-subtle italic col-span-full">No branch types available</span>
                  )}
                </div>
              </div>
            )}
          </div>
          {hasLegacyPerms && (
            <div className="border border-amber-400 bg-amber-50 px-3 py-2 text-[11px] text-amber-900" data-testid="role-legacy-banner">
              <b>Legacy permissions detected.</b> We&apos;ve auto-mapped them to the new grid.
              Save the role once to clear the warning (the old names stay valid during migration).
            </div>
          )}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
              Permissions · {perms.size} selected
            </div>
            {isBuiltinAdmin && (
              <p className="font-mono text-[10px] text-ink-subtle mb-2">
                Admin always has full access — this role cannot be downgraded.
              </p>
            )}
            <div className="space-y-2">
              {catalog && Object.entries(catalog.modules).map(([mod, actions]) => {
                const { byVerb, workflows } = buildModuleGrid(mod, actions);
                const moduleKeys = Object.keys(actions)
                  .filter((a) => !isLegacyPerm(`${mod}.${a}`))
                  .map((a) => `${mod}.${a}`);
                const allSelected = moduleKeys.length > 0 && moduleKeys.every((k) => perms.has(k));
                const anySelected = moduleKeys.some((k) => perms.has(k));
                return (
                  <div key={mod} data-testid={`role-perm-module-${mod}`} className="border border-canvas-line">
                    <div
                      data-testid={`role-perm-module-toggle-${mod}`}
                      className="w-full px-3 py-2 bg-canvas-panel flex items-center justify-between"
                    >
                      <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink font-semibold capitalize">
                        {mod.replace(/_/g, " ")}
                      </span>
                      <span className="font-mono text-[9px] text-ink-muted">
                        {allSelected ? "ALL" : anySelected ? "PARTIAL" : "NONE"}
                      </span>
                    </div>
                    <div className="px-3 py-2">
                      <div className="grid grid-cols-4 gap-1 text-[11px]">
                        {VERBS.map((verb) => {
                          const entry = byVerb[verb];
                          if (!entry) {
                            return (
                              <div key={verb} className="flex items-center justify-center text-ink-subtle text-[10px] font-mono uppercase opacity-30 py-1">
                                —
                              </div>
                            );
                          }
                          const [key, label] = entry;
                          return (
                            <label key={key} className="flex items-center gap-1.5 cursor-pointer border border-canvas-line px-2 py-1 hover:border-ink" title={label}>
                              <input
                                type="checkbox"
                                checked={perms.has(key)}
                                onChange={() => toggle(key)}
                                data-testid={`perm-${key}`}
                                className="accent-ink"
                              />
                              <span className="font-mono text-[10px] uppercase tracking-wider">{verb}</span>
                            </label>
                          );
                        })}
                      </div>
                      {workflows.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-canvas-line flex flex-wrap gap-1.5">
                          {workflows.map(([action, key, label]) => (
                            <label key={key} className="inline-flex items-center gap-1.5 cursor-pointer border border-amber-400 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-900 hover:border-amber-600" title={label}>
                              <input
                                type="checkbox"
                                checked={perms.has(key)}
                                onChange={() => toggle(key)}
                                data-testid={`perm-${key}`}
                                className="accent-ink h-3 w-3"
                              />
                              <span className="font-mono uppercase tracking-wider">{action.replace(/_/g, " ")}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="pt-4 border-t border-canvas-line flex justify-end gap-2 sticky bottom-0 bg-canvas-card -mx-6 px-6 -mb-6 pb-6">
            <button type="button" onClick={onClose} className="px-3 py-2 border border-canvas-line text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="role-save"
              className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Save role" : "Create role"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [branchTypes, setBranchTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, bt] = await Promise.all([
        api.get("/roles"),
        api.get("/roles/catalog"),
        api.get("/roles/branch-types-catalog"),
      ]);
      setRoles(r.data);
      setCatalog(c.data);
      setBranchTypes(bt.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await api.delete(`/roles/${role.id}`);
      toast.success("Role deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="roles-page">
      <PageHeader
        eyebrow="10 / Access"
        title="Roles & Permissions"
        subtitle="Define what each role can do across the app. Admins always have full access."
        actions={
          <button
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
            data-testid="add-role-button"
            className="bg-ink text-white px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-brand transition-colors duration-150 flex items-center gap-2"
          >
            <Plus size={14} weight="bold" />
            New Role
          </button>
        }
      />

      <div className="px-8 py-6">
        {loading && <div className="font-mono text-xs text-ink-subtle">Loading…</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((r) => (
            <div
              key={r.id}
              data-testid={`role-card-${r.id}`}
              className="border border-canvas-line bg-canvas-card p-4 hover:border-ink transition-colors"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={14} weight="bold" className="text-ink" />
                    <h3 className="font-display text-lg font-bold text-ink capitalize">{r.name}</h3>
                  </div>
                  {r.is_builtin && (
                    <span className="inline-block mt-1 mr-1 px-2 py-0.5 border border-ink/30 text-[9px] font-mono uppercase tracking-wider text-ink-muted">
                      Built-in
                    </span>
                  )}
                  {r.scope === "all_branches" && (
                    <span
                      data-testid={`role-scope-badge-${r.id}`}
                      className="inline-block mt-1 px-2 py-0.5 border border-ink bg-ink text-white text-[9px] font-mono uppercase tracking-wider"
                    >
                      All branches
                    </span>
                  )}
                  {r.scope === "branch_type" && (
                    <span
                      data-testid={`role-scope-badge-${r.id}`}
                      className="inline-block mt-1 px-2 py-0.5 border border-emerald-700 bg-emerald-600 text-white text-[9px] font-mono uppercase tracking-wider"
                    >
                      Branch-type · {(r.branch_type_codes || []).join(" · ") || "none"}
                    </span>
                  )}
                  {r.has_legacy_perms && (
                    <span
                      data-testid={`role-legacy-badge-${r.id}`}
                      className="inline-block mt-1 ml-1 px-2 py-0.5 border border-amber-400 bg-amber-50 text-amber-900 text-[9px] font-mono uppercase tracking-wider"
                      title="This role still uses legacy permission names — open and save to migrate."
                    >
                      Legacy
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      setEditing(r);
                      setDrawerOpen(true);
                    }}
                    data-testid={`edit-role-${r.id}`}
                    title="Edit"
                    className="p-1.5 border border-canvas-line hover:border-ink"
                  >
                    <PencilSimple size={11} weight="bold" />
                  </button>
                  {!r.is_builtin && (
                    <button
                      onClick={() => onDelete(r)}
                      data-testid={`delete-role-${r.id}`}
                      title="Delete"
                      className="p-1.5 border border-destructive text-destructive hover:bg-destructive hover:text-white"
                    >
                      <Trash size={11} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-ink-muted mb-3">{r.description || "—"}</p>
              <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-ink-subtle">
                <span data-testid={`role-perm-count-${r.id}`}>
                  {r.permissions?.length || 0} permissions
                </span>
                <span data-testid={`role-user-count-${r.id}`}>{r.user_count || 0} users</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <RoleFormDrawer
        open={drawerOpen}
        editing={editing}
        catalog={catalog}
        branchTypes={branchTypes}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          setDrawerOpen(false);
          load();
        }}
      />
    </div>
  );
}
